import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useAuthStore } from "@/states/auth";

export type NotificationType =
  | "followup"
  | "customer_payment"
  | "supplier_payment"
  | "reminder"
  | "general";

export interface NotificationItem {
  id: string;
  tenantId: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  data: {
    leadId?: number;
    scheduleId?: string;
    reminderId?: string;
    link?: string;
    [key: string]: unknown;
  } | null;
  triggeredAt: string;
  expiresAt: string;
  isAlertDismissed: boolean;
  alertDismissedAt: string | null;
  isRead: boolean;
  readAt: string | null;
  sourceType: string | null;
  sourceId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationsResponse {
  notifications: NotificationItem[];
  unreadCount: number;
  activeAlerts: NotificationItem[];
  activeAlertCount: number;
}

export interface UserReminderItem {
  id: string;
  tenantId: string;
  userId: string;
  title: string;
  description: string | null;
  remindAt: string;
  link: string | null;
  status: "pending" | "triggered" | "cancelled";
  createdAt: string;
  updatedAt: string;
}

export interface CreateReminderPayload {
  title: string;
  description?: string;
  remindAt: string;
  link?: string;
}

export const NOTIFICATIONS_QUERY_KEY = ["notifications"];
export const ALL_NOTIFICATIONS_QUERY_KEY = ["all_notifications"];
export const REMINDERS_QUERY_KEY = ["reminders"];

export interface AllNotificationsResponse {
  notifications: NotificationItem[];
  total: number;
  unreadCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AllNotificationsParams {
  page?: number;
  limit?: number;
  type?: string;
  unread?: boolean;
  search?: string;
}

/** Partitions private notification caches by the authenticated tenant and user. */
function useNotificationScope() {
  const user = useAuthStore((state) => state.user);
  return { key: [user?.tenant?.id, user?.id], enabled: Boolean(user?.tenant?.id && user.role !== "super_admin") };
}

/**
 * Fetches all notifications (with pagination and filters) for the Notifications page.
 */
export const useAllNotificationsQuery = (
  params: AllNotificationsParams = {},
  enabled: boolean = true
) => {
  const scope = useNotificationScope();
  return useQuery<AllNotificationsResponse>({
    queryKey: [...ALL_NOTIFICATIONS_QUERY_KEY, ...scope.key, params],
    queryFn: async () => {
      const response = await axios.get<AllNotificationsResponse>("/api/notifications", {
        params: {
          all: "true",
          page: params.page || 1,
          limit: params.limit || 20,
          type: params.type || undefined,
          unread: params.unread ? "true" : undefined,
          search: params.search || undefined,
        },
      });
      return response.data;
    },
    enabled: enabled && scope.enabled,
    refetchInterval: 30_000,
  });
};

/**
 * Shares active notifications; the layout owns polling and other observers reuse its cache.
 */
export const useNotificationsQuery = ({ enabled = true, poll = false }: { enabled?: boolean; poll?: boolean } = {}) => {
  const scope = useNotificationScope();
  return useQuery<NotificationsResponse>({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, ...scope.key],
    queryFn: async () => {
      const response = await axios.get<NotificationsResponse>("/api/notifications");
      return response.data;
    },
    enabled: enabled && scope.enabled,
    refetchInterval: poll ? 20_000 : false,
    staleTime: 10_000,
  });
};

/**
 * Dismisses an on-screen alert banner.
 * Does not remove the notification from the 24h drop-down.
 */
export const useDismissAlertMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await axios.patch(`/api/notifications/${notificationId}/dismiss-alert`);
      return response.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ALL_NOTIFICATIONS_QUERY_KEY });
    },
  });
};

/**
 * Marks a single notification as read.
 */
export const useMarkNotificationReadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await axios.patch(`/api/notifications/${notificationId}/read`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ALL_NOTIFICATIONS_QUERY_KEY });
    },
  });
};

/**
 * Marks all notifications as read.
 */
export const useMarkAllNotificationsReadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await axios.post("/api/notifications/read-all");
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ALL_NOTIFICATIONS_QUERY_KEY });
    },
  });
};

/**
 * Deletes a notification.
 */
export const useDeleteNotificationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await axios.delete(`/api/notifications/${notificationId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ALL_NOTIFICATIONS_QUERY_KEY });
    },
  });
};

/**
 * List user reminders.
 */
export const useRemindersQuery = (enabled: boolean = true) => {
  const scope = useNotificationScope();
  return useQuery<UserReminderItem[]>({
    queryKey: [...REMINDERS_QUERY_KEY, ...scope.key],
    queryFn: async () => {
      const response = await axios.get<UserReminderItem[]>("/api/reminders");
      return response.data;
    },
    enabled: enabled && scope.enabled,
  });
};

/**
 * Creates a new user reminder.
 */
export const useCreateReminderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateReminderPayload) => {
      const response = await axios.post<UserReminderItem>("/api/reminders", payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REMINDERS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });
};

/**
 * Deletes a reminder.
 */
export const useDeleteReminderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reminderId: string) => {
      const response = await axios.delete(`/api/reminders/${reminderId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REMINDERS_QUERY_KEY });
    },
  });
};
